import { usePaginatedQuery, useQuery } from "convex/react";
import {
	ArchiveIcon,
	ArchiveXIcon,
	FileIcon,
	InboxIcon,
	SearchIcon,
	SendIcon,
	SettingsIcon,
	Trash2Icon,
} from "lucide-react";
import { useMemo, useState } from "react";
import { Link } from "react-router";

import { Input } from "@/components/ui/input";
import {
	ResizableHandle,
	ResizablePanel,
	ResizablePanelGroup,
} from "@/components/ui/resizable";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { TooltipProvider } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { api } from "../../../../convex/_generated/api";
import type { Id } from "../../../../convex/_generated/dataModel";
import { AccountSwitcher } from "./account-switcher";
import { MailDisplay } from "./mail-display";
import { MailList } from "./mail-list";
import { Nav } from "./nav";
import type { MailAccount, MailFolder, MailMessageDetail } from "./types";
import { MailProvider, useMail } from "./use-mail";

interface MailProps {
	accounts: MailAccount[];
	defaultLayout?: number[];
	defaultCollapsed?: boolean;
	navCollapsedSize: number;
}

export function Mail(props: MailProps) {
	return (
		<MailProvider>
			<MailInner {...props} />
		</MailProvider>
	);
}

type FolderDef = {
	key: MailFolder;
	title: string;
	icon: typeof InboxIcon;
};

const FOLDERS: FolderDef[] = [
	{ key: "inbox", title: "Inbox", icon: InboxIcon },
	{ key: "drafts", title: "Drafts", icon: FileIcon },
	{ key: "sent", title: "Sent", icon: SendIcon },
	{ key: "junk", title: "Junk", icon: ArchiveXIcon },
	{ key: "trash", title: "Trash", icon: Trash2Icon },
	{ key: "archive", title: "Archive", icon: ArchiveIcon },
];

function MailInner({
	accounts,
	defaultLayout = [20, 32, 48],
	defaultCollapsed = false,
	navCollapsedSize,
}: MailProps) {
	const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed);
	const [selectedConnectionId, setSelectedConnectionId] = useState<
		Id<"oauthConnections">
	>(() => accounts[0].connectionId);
	const [folder, setFolder] = useState<MailFolder>("inbox");
	const [tab, setTab] = useState<"all" | "unread">("all");
	const [mail, setMail] = useMail();

	const counts = useQuery(api.mail.queries.folderCounts, {
		connectionId: selectedConnectionId,
	});

	const pagination = usePaginatedQuery(
		api.mail.queries.listMessages,
		{
			folder,
			unreadOnly: tab === "unread",
			connectionId: selectedConnectionId,
		},
		{ initialNumItems: 25 },
	);

	const detail = useQuery(
		api.mail.queries.getMessage,
		mail.selected ? { messageId: mail.selected } : "skip",
	);

	const navLinks = useMemo(
		() =>
			FOLDERS.map((f) => {
				const label = counts?.[f.key]
					? `${counts[f.key].unread > 0 ? `${counts[f.key].unread}/` : ""}${counts[f.key].total}`
					: "";
				return {
					title: f.title,
					label,
					icon: f.icon,
					variant: folder === f.key ? ("default" as const) : ("ghost" as const),
					onSelect: () => {
						setFolder(f.key);
						setMail({ selected: null });
					},
				};
			}),
		[counts, folder, setMail],
	);

	const items = pagination.results.map((row) => ({
		_id: row._id,
		connectionId: row.connectionId,
		provider: row.provider,
		providerThreadId: row.providerThreadId,
		fromEmail: row.fromEmail,
		fromName: row.fromName,
		subject: row.subject,
		snippet: row.snippet,
		receivedAt: row.receivedAt,
		folder: row.folder,
		labels: row.labels,
		isRead: row.isRead,
		hasAttachments: row.hasAttachments,
	}));

	return (
		<TooltipProvider delay={0}>
			<ResizablePanelGroup
				orientation="horizontal"
				className="h-full min-h-0 items-stretch"
			>
				<ResizablePanel
					defaultSize={`${defaultLayout[0]}%`}
					collapsedSize={`${navCollapsedSize}%`}
					collapsible
					minSize="15%"
					maxSize="20%"
					onResize={(size) =>
						setIsCollapsed(size.asPercentage <= navCollapsedSize)
					}
					className={cn(
						"flex min-h-0 flex-col",
						isCollapsed &&
							"min-w-[50px] transition-all duration-300 ease-in-out",
					)}
				>
					<div
						className={cn(
							"flex h-[52px] items-center justify-center",
							isCollapsed ? "h-[52px]" : "pr-2 pl-10",
						)}
					>
						<AccountSwitcher
							isCollapsed={isCollapsed}
							accounts={accounts}
							selectedConnectionId={selectedConnectionId}
							onSelect={(id) => {
								setSelectedConnectionId(id);
								setMail({ selected: null });
							}}
						/>
					</div>
					<Separator />
					<ScrollArea className="min-h-0 flex-1">
						<Nav isCollapsed={isCollapsed} links={navLinks} />
						<Separator />
						<div className="p-2">
							<Link
								to="/staff/mail/settings"
								className={cn(
									"inline-flex items-center gap-2 rounded px-2 py-1 text-xs text-muted-foreground hover:bg-accent",
									isCollapsed && "w-9 justify-center p-2",
								)}
								title="Mail settings"
							>
								<SettingsIcon className="h-4 w-4" />
								{!isCollapsed && <span>Mail settings</span>}
							</Link>
						</div>
					</ScrollArea>
				</ResizablePanel>
				<ResizableHandle withHandle />
				<ResizablePanel defaultSize={`${defaultLayout[1]}%`} minSize="30%">
					<Tabs
						value={tab}
						onValueChange={(v) => setTab(v === "unread" ? "unread" : "all")}
						className="flex h-full min-h-0 flex-col gap-0"
					>
						<div className="flex h-[52px] items-center px-4">
							<h1 className="text-xl font-bold capitalize">{folder}</h1>
							<TabsList className="ml-auto">
								<TabsTrigger value="all" className="text-muted-foreground">
									All mail
								</TabsTrigger>
								<TabsTrigger value="unread" className="text-muted-foreground">
									Unread
								</TabsTrigger>
							</TabsList>
						</div>
						<Separator />
						<div className="bg-background/95 p-4 backdrop-blur supports-[backdrop-filter]:bg-background/60">
							<div className="relative">
								<SearchIcon className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
								<Input
									placeholder="Search (coming soon)"
									className="pl-8"
									disabled
								/>
							</div>
						</div>
						<TabsContent
							value={tab}
							className="m-0 flex min-h-0 flex-1 flex-col"
						>
							<MailList
								items={items}
								isLoading={pagination.status === "LoadingFirstPage"}
								canLoadMore={pagination.status === "CanLoadMore"}
								onLoadMore={() => pagination.loadMore(25)}
							/>
						</TabsContent>
					</Tabs>
				</ResizablePanel>
				<ResizableHandle withHandle />
				<ResizablePanel defaultSize={`${defaultLayout[2]}%`}>
					<MailDisplay
						message={(detail as MailMessageDetail | null | undefined) ?? null}
						isLoading={mail.selected !== null && detail === undefined}
					/>
				</ResizablePanel>
			</ResizablePanelGroup>
		</TooltipProvider>
	);
}
